<template>
  <div v-if="isEditMode" class="mt-3 space-y-3">
    <!-- 配置编辑区域 -->
    <div class="p-2 bg-surface-100 rounded">
      <!-- 节点信息 -->
      <div class="text-xs mb-3">
        <h5 class="text-xs font-semibold text-surface-600 mb-3">
          {{ $t('workflowApps.nodeInformation') }}
        </h5>

        <!-- 技术信息 -->
        <div class="text-surface-500 mb-3">
          <div class="flex items-center gap-2">
            <span class="font-medium">{{ $t('workflowApps.inputId') }}:</span>
            <span v-if="!showInputIdEditor" class="ml-1">{{ input.id }}</span>
            <InputText
              v-else
              v-model="localInput.id"
              size="small"
              class="flex-1 max-w-xs"
              @blur="handleInputIdBlur"
              @keyup.enter="handleInputIdBlur"
            />
            <Button
              :icon="showInputIdEditor ? 'pi pi-check' : 'pi pi-pencil'"
              size="small"
              text
              severity="secondary"
              :class="{ 'text-primary-600': showInputIdEditor }"
              @click="toggleInputIdEditor"
            />
          </div>
        </div>
        <!-- 顺序设置 -->
        <div class="text-surface-500 flex items-center gap-2">
          <span class="font-medium">{{ $t('workflowApps.order') }}:</span>
          <InputNumber
            v-model="localInput.config.order"
            :min="0"
            :max="999"
            size="small"
            class="order-input w-32"
            @update:model-value="updateInputConfig('config.order', $event)"
          />
          <div class="flex items-center gap-1 ml-1">
            <Button
              v-tooltip.bottom="$t('workflowApps.moveUp')"
              icon="pi pi-arrow-up"
              size="small"
              text
              severity="secondary"
              :disabled="!canMoveUp"
              @click="$emit('move-up')"
            />
            <Button
              v-tooltip.bottom="$t('workflowApps.moveDown')"
              icon="pi pi-arrow-down"
              size="small"
              text
              severity="secondary"
              :disabled="!canMoveDown"
              @click="$emit('move-down')"
            />
          </div>
        </div>
      </div>

      <!-- 类型设置 -->
      <div class="mb-3">
        <div class="flex items-center gap-2 text-surface-500">
          <span class="font-medium text-xs"
            >{{ $t('workflowApps.type') }}:</span
          >
          <Select
            v-model="localInput.type"
            :options="inputTypeOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="w-32"
            @update:model-value="updateInputConfig('type', $event)"
          />
        </div>
      </div>

      <h5 class="text-xs font-semibold text-surface-600 mb-3 mt-3">
        {{ $t('workflowApps.configurationSettings') }}
      </h5>

      <!-- Number类型配置 -->
      <div
        v-if="
          localInput.type === 'number' ||
          localInput.type === 'int' ||
          localInput.type === 'float'
        "
        class="space-y-2"
      >
        <div class="grid grid-cols-2 gap-2">
          <div>
            <div class="flex items-center gap-1 mb-1">
              <label class="text-xs text-surface-500">{{
                $t('workflowApps.defaultValue')
              }}</label>
              <i
                v-tooltip.bottom="$t('workflowApps.defaultValueDescription')"
                class="pi pi-info-circle text-xs text-surface-400 cursor-help"
              ></i>
            </div>
            <InputNumber
              v-model="localInput.value"
              :min="localInput.config?.min || 0"
              :max="localInput.config?.max || 1000000"
              :step="localInput.config?.step || 1"
              size="small"
              class="w-full"
              @update:model-value="updateInputConfig('value', $event)"
            />
          </div>
          <div>
            <label class="text-xs text-surface-500 block mb-1">{{
              $t('workflowApps.step')
            }}</label>
            <InputNumber
              v-model="localInput.config.step"
              :min="0.001"
              :max="1000"
              :step="0.001"
              size="small"
              class="w-full"
              @update:model-value="updateInputConfig('config.step', $event)"
            />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-xs text-surface-500 block mb-1">{{
              $t('workflowApps.minValue')
            }}</label>
            <InputNumber
              v-model="localInput.config.min"
              :min="0"
              :max="1000000"
              size="small"
              class="w-full"
              @update:model-value="updateInputConfig('config.min', $event)"
            />
          </div>
          <div>
            <label class="text-xs text-surface-500 block mb-1">{{
              $t('workflowApps.maxValue')
            }}</label>
            <InputNumber
              v-model="localInput.config.max"
              :min="0"
              :max="1000000"
              size="small"
              class="w-full"
              @update:model-value="updateInputConfig('config.max', $event)"
            />
          </div>
        </div>
      </div>

      <!-- 通用配置设置 -->
      <div class="space-y-3 mt-3">
        <!-- 默认值配置 -->
        <div>
          <div class="flex items-center gap-1 mb-1.5">
            <label class="text-xs text-surface-500">{{
              $t('workflowApps.defaultValueConfig')
            }}</label>
            <i
              v-tooltip.bottom="
                $t('workflowApps.defaultValueConfigDescription')
              "
              class="pi pi-info-circle text-xs text-surface-400 cursor-help"
            ></i>
          </div>
          <InputText
            v-model="localInput.config.defaultValue"
            size="small"
            class="w-full"
            :placeholder="$t('workflowApps.defaultValueConfigPlaceholder')"
            @update:model-value="
              updateInputConfig('config.defaultValue', $event)
            "
          />
        </div>

        <!-- 文本类型最大字数 -->
        <div
          v-if="
            localInput.type === 'text' ||
            localInput.type === 'string' ||
            localInput.type === 'customtext' ||
            localInput.type === 'multiline'
          "
        >
          <div class="flex items-center gap-1 mb-1.5">
            <label class="text-xs text-surface-500">{{
              $t('officialAppsManagement.editFormDialog.maxLength')
            }}</label>
          </div>
          <InputNumber
            v-model="localInput.config.maxLength"
            :min="1"
            :use-grouping="false"
            size="small"
            class="w-full"
            @update:model-value="updateInputConfig('config.maxLength', $event)"
          />
        </div>
      </div>

      <!-- 默认值设置（所有类型） -->
      <div class="mt-3">
        <div class="flex items-center gap-1 mb-1">
          <label class="text-xs text-surface-500">{{
            $t('workflowApps.defaultValue')
          }}</label>
          <i
            v-tooltip.bottom="$t('workflowApps.defaultValueDescription')"
            class="pi pi-info-circle text-xs text-surface-400 cursor-help"
          ></i>
        </div>
        <!-- Text类型默认值 -->
        <Textarea
          v-if="
            localInput.type === 'text' ||
            localInput.type === 'string' ||
            localInput.type === 'customtext' ||
            localInput.type === 'multiline'
          "
          v-model="localInput.value"
          size="small"
          :auto-resize="true"
          :rows="1"
          class="w-full"
          @update:model-value="updateInputConfig('value', $event)"
        />
        <!-- Number类型默认值 -->
        <InputNumber
          v-else-if="
            localInput.type === 'number' ||
            localInput.type === 'int' ||
            localInput.type === 'float'
          "
          v-model="localInput.value"
          :min="localInput.config?.min || 0"
          :max="localInput.config?.max || 1000000"
          :step="localInput.config?.step || 1"
          size="small"
          class="w-full"
          @update:model-value="updateInputConfig('value', $event)"
        />
        <!-- Combo类型默认值 -->
        <Select
          v-else-if="localInput.type === 'combo'"
          v-model="localInput.value"
          :options="localInput.options"
          option-label="label"
          option-value="value"
          size="small"
          class="w-full"
          @update:model-value="updateInputConfig('value', $event)"
        />
        <!-- Boolean类型默认值 -->
        <ToggleSwitch
          v-else-if="
            localInput.type === 'boolean' || localInput.type === 'bool'
          "
          v-model="localInput.value"
          size="small"
          @update:model-value="updateInputConfig('value', $event)"
        />
        <!-- File.image类型默认值 -->
        <div
          v-else-if="localInput.type === 'file.image'"
          class="text-xs text-surface-500"
        >
          {{ $t('workflowApps.fileImageNote') }}
        </div>
        <!-- File.audio.select 类型：默认值选择 + 预设音频选项 -->
        <div
          v-else-if="localInput.type === 'file.audio.select'"
          class="space-y-2"
        >
          <!-- 默认值：与运行时相同的选择+上传 UI -->
          <div class="space-y-1.5">
            <div class="text-xs text-surface-500 font-medium">
              {{ $t('workflowApps.defaultValue') }}
            </div>
            <Select
              :model-value="defaultValueSelectDisplay"
              :options="defaultValueAudioOptions"
              option-label="label"
              option-value="value"
              :placeholder="$t('workflowApps.selectAudio')"
              size="small"
              class="w-full"
              @update:model-value="handleDefaultValueSelectChange"
            />
            <!-- 选择自定义时显示上传 -->
            <div
              v-if="isDefaultValueCustomMode"
              class="flex items-center gap-2 p-2 rounded bg-surface-100"
            >
              <span
                v-if="localInput.value"
                class="flex-1 truncate text-xs"
                :title="localInput.value"
              >
                {{ localInput.value }}
              </span>
              <span v-else class="flex-1 text-xs text-surface-400">
                {{ $t('workflowApps.notSet') }}
              </span>
              <Button
                :icon="
                  presetUploadingDefaultValue
                    ? 'pi pi-spin pi-spinner'
                    : 'pi pi-upload'
                "
                size="small"
                severity="secondary"
                text
                :disabled="presetUploadingDefaultValue"
                :title="$t('workflowApps.uploadAudio')"
                @click="triggerDefaultValueAudioUpload"
              />
            </div>
            <input
              ref="defaultValueAudioInputRef"
              type="file"
              accept="audio/*"
              class="hidden"
              @change="handleDefaultValueAudioFileSelect"
            />
          </div>
          <div class="text-xs text-surface-500">
            {{ $t('workflowApps.fileAudioSelectNote') }}
          </div>
          <div class="flex justify-between items-center mb-2">
            <span class="text-xs text-surface-500 font-medium">{{
              $t('workflowApps.audioPresetOptions')
            }}</span>
            <Button
              icon="pi pi-plus"
              size="small"
              severity="secondary"
              text
              @click="$emit('add-combo-option')"
            />
          </div>
          <div class="space-y-1.5">
            <div
              v-for="(option, idx) in presetAudioOptions"
              :key="idx"
              class="flex items-center gap-2"
            >
              <InputText
                :model-value="option.option.label"
                size="small"
                class="flex-1"
                :placeholder="$t('workflowApps.optionLabel')"
                @update:model-value="
                  $emit(
                    'update-combo-option',
                    option.actualIndex,
                    'label',
                    $event || ''
                  )
                "
              />
              <div class="flex flex-1 items-center gap-1 min-w-0">
                <span
                  v-if="option.option.value"
                  class="flex-1 truncate text-xs text-surface-600"
                  :title="option.option.value"
                >
                  {{ option.option.value }}
                </span>
                <span v-else class="flex-1 text-xs text-surface-400">
                  {{ $t('workflowApps.notSet') }}
                </span>
                <Button
                  :icon="
                    presetUploadingOptionIndex === option.actualIndex
                      ? 'pi pi-spin pi-spinner'
                      : 'pi pi-upload'
                  "
                  size="small"
                  severity="secondary"
                  text
                  :disabled="presetUploadingOptionIndex !== null"
                  :title="
                    presetUploadingOptionIndex === option.actualIndex
                      ? $t('workflowApps.uploading')
                      : $t('workflowApps.uploadAudio')
                  "
                  @click="triggerPresetAudioUpload(option.actualIndex)"
                />
              </div>
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                @click="$emit('remove-combo-option', option.actualIndex)"
              />
            </div>
          </div>
          <input
            ref="presetAudioInputRef"
            type="file"
            accept="audio/*"
            class="hidden"
            @change="handlePresetAudioFileSelect"
          />
          <div class="flex gap-2 items-center">
            <span class="text-xs text-surface-500">{{
              $t('workflowApps.maxDuration')
            }}</span>
            <InputNumber
              v-model="localInput.config.maxDuration"
              :min="1"
              :use-grouping="false"
              input-class="w-full"
              class="flex-1"
              size="small"
              @update:model-value="
                updateInputConfig('config.maxDuration', $event)
              "
            />
            <span class="text-xs text-surface-500">s</span>
          </div>
        </div>
        <!-- File.audio / File.video 类型默认值 -->
        <div
          v-else-if="
            localInput.type === 'file.audio' || localInput.type === 'file.video'
          "
          class="text-xs text-surface-500 space-y-1"
        >
          <div>
            {{
              localInput.type === 'file.audio'
                ? $t('workflowApps.fileAudioNote')
                : $t('workflowApps.fileVideoNote')
            }}
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-surface-500">{{
              $t('workflowApps.maxDuration')
            }}</span>
            <InputNumber
              v-model="localInput.config.maxDuration"
              :min="1"
              :use-grouping="false"
              input-class="w-full"
              class="flex-1"
              size="small"
              @update:model-value="
                updateInputConfig('config.maxDuration', $event)
              "
            />
            <span class="text-xs text-surface-500">s</span>
          </div>
        </div>
        <!-- 其他类型使用Textarea -->
        <Textarea
          v-else
          v-model="localInput.value"
          :rows="1"
          auto-resize
          size="small"
          class="w-full"
          @update:model-value="updateInputConfig('value', $event)"
        />
      </div>

      <!-- 可用选项（Combo 类型） -->
      <div v-if="localInput.type === 'combo'" class="mt-3">
        <div class="flex justify-between items-center mb-2">
          <span class="text-xs text-surface-500 font-medium">{{
            $t('workflowApps.availableOptions')
          }}</span>
          <Button
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            text
            @click="$emit('add-combo-option')"
          />
        </div>
        <div class="space-y-1.5">
          <div
            v-for="(option, index) in localInput.options"
            :key="index"
            class="flex items-center gap-2"
          >
            <InputText
              v-model="option.label"
              size="small"
              class="flex-1"
              @update:model-value="
                $emit('update-combo-option', index, 'label', $event || '')
              "
            />
            <InputText
              v-model="option.value"
              size="small"
              class="flex-1"
              @update:model-value="
                $emit('update-combo-option', index, 'value', $event || '')
              "
            />
            <Button
              icon="pi pi-trash"
              size="small"
              severity="danger"
              text
              @click="$emit('remove-combo-option', index)"
            />
          </div>
        </div>
      </div>

      <!-- 是否必填 -->
      <div class="flex items-center gap-2 mt-3">
        <label class="text-xs text-surface-500">{{
          $t('workflowApps.required')
        }}</label>
        <ToggleSwitch
          v-model="localInput.config.required"
          size="small"
          @update:model-value="updateInputConfig('config.required', $event)"
        />
      </div>

      <!-- LLM优化提示词配置（仅文本类型） -->
      <div
        v-if="
          localInput.type === 'customtext' ||
          localInput.type === 'text' ||
          localInput.type === 'string' ||
          localInput.type === 'multiline'
        "
        class="mt-2"
      >
        <div class="flex items-center gap-1 mb-1">
          <label class="text-xs text-surface-500">{{
            $t('workflowApps.llmPrompt')
          }}</label>
          <i
            v-tooltip.bottom="$t('workflowApps.llmPromptDescription')"
            class="pi pi-info-circle text-xs text-surface-400 cursor-help"
          ></i>
        </div>
        <Textarea
          v-model="localInput.config.llmPrompt"
          :rows="2"
          auto-resize
          size="small"
          class="w-full"
          :placeholder="$t('workflowApps.llmPromptPlaceholder')"
          @update:model-value="updateInputConfig('config.llmPrompt', $event)"
        />
      </div>

      <!-- 描述编辑 -->
      <div class="mt-2">
        <label class="text-xs text-surface-500 block mb-1">{{
          $t('workflowApps.description')
        }}</label>
        <Textarea
          v-model="localInput.description"
          :rows="1"
          auto-resize
          size="small"
          class="w-full"
          @update:model-value="updateInputConfig('description', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

interface WorkflowInput {
  id: string
  name: string
  type: string
  value: any
  description?: string
  placeholder?: string
  options?: any[]
  order?: number
  nodeId?: string
  widgetName?: string
  config: {
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    required?: boolean
    order?: number
    llmPrompt?: string
    [key: string]: any
  }
}

const AUDIO_SELECT_CUSTOM_VALUE = '__custom__'

const props = withDefaults(
  defineProps<{
    input: WorkflowInput
    isEditMode?: boolean
    inputTypeOptions?: Array<{ label: string; value: string }>
    canMoveUp?: boolean
    canMoveDown?: boolean
    presetUploadingFor?: {
      inputId: string
      optionIndex?: number
      type?: 'default'
    } | null
  }>(),
  {
    isEditMode: false,
    inputTypeOptions: () => [],
    canMoveUp: false,
    canMoveDown: false,
    presetUploadingFor: null
  }
)

const emit = defineEmits<{
  'update-config': [property: string, value: any]
  'move-up': []
  'move-down': []
  'add-combo-option': []
  'update-combo-option': [
    index: number,
    property: 'label' | 'value',
    value: string
  ]
  'remove-combo-option': [index: number]
  'upload-audio-for-option': [optionIndex: number, file: File]
  'upload-audio-for-default-value': [file: File]
  'update-input-id': [inputId: string, nodeId: string, widgetName: string]
}>()

// 创建本地响应式副本
const localInput = reactive<WorkflowInput>(
  JSON.parse(JSON.stringify(props.input))
)

// 控制 inputId 编辑器的显示/隐藏
const showInputIdEditor = ref(false)

// 监听 props.input 的变化
watch(
  () => props.input,
  (newInput) => {
    const oldId = localInput.id
    Object.assign(localInput, JSON.parse(JSON.stringify(newInput)))
    // 如果 input.id 发生变化（可能是外部更新），关闭编辑器
    if (showInputIdEditor.value && oldId !== newInput.id) {
      showInputIdEditor.value = false
    }
  },
  { deep: true }
)

// 切换 inputId 编辑器的显示/隐藏
const toggleInputIdEditor = () => {
  if (showInputIdEditor.value) {
    // 如果正在编辑，保存更改
    handleInputIdBlur()
  } else {
    // 如果未编辑，打开编辑器
    showInputIdEditor.value = true
  }
}

// 处理 inputId 输入框失焦或回车
const handleInputIdBlur = () => {
  if (showInputIdEditor.value && localInput.id !== props.input.id) {
    // 解析新的 inputId，格式通常是 {nodeId}-{widgetName}
    const newInputId = localInput.id.trim()
    const parts = newInputId.split('-')

    let newNodeId = ''
    let newWidgetName = ''

    if (parts.length >= 2) {
      // 如果包含多个 '-'，最后一个部分是 widgetName，前面的是 nodeId
      newWidgetName = parts[parts.length - 1]
      newNodeId = parts.slice(0, -1).join('-')
    } else if (parts.length === 1) {
      // 如果只有一个部分，假设整个是 nodeId，widgetName 为空
      newNodeId = parts[0]
      newWidgetName = ''
    }

    console.log('[InputConfigEditor] handleInputIdBlur - 解析 inputId:', {
      oldInputId: props.input.id,
      newInputId,
      newNodeId,
      newWidgetName,
      parts
    })

    // 更新本地数据
    localInput.nodeId = newNodeId
    localInput.widgetName = newWidgetName

    console.log(
      '[InputConfigEditor] handleInputIdBlur - 更新后的 localInput:',
      {
        id: localInput.id,
        nodeId: localInput.nodeId,
        widgetName: localInput.widgetName
      }
    )

    // 如果值发生变化，通知父组件更新（包含 nodeId 和 widgetName）
    emit('update-input-id', localInput.id, newNodeId, newWidgetName)
  }
  showInputIdEditor.value = false
}

const updateInputConfig = (property: string, value: any) => {
  emit('update-config', property, value)
}

const { t } = useI18n()
const presetAudioInputRef = ref<HTMLInputElement>()
const defaultValueAudioInputRef = ref<HTMLInputElement>()
const pendingPresetUploadIndex = ref<number | null>(null)

// 上传中状态（从父组件传入）
const presetUploadingOptionIndex = computed(() => {
  if (
    props.presetUploadingFor?.inputId === props.input.id &&
    typeof props.presetUploadingFor?.optionIndex === 'number'
  ) {
    return props.presetUploadingFor.optionIndex
  }
  return null
})

const presetUploadingDefaultValue = computed(
  () =>
    props.presetUploadingFor?.inputId === props.input.id &&
    props.presetUploadingFor?.type === 'default'
)

// 默认值选择：选项列表（预设 + 自定义）
const defaultValueAudioOptions = computed(() => {
  if (props.input.type !== 'file.audio.select') return []
  const presetOptions = (props.input.options || []).filter(
    (o: { value?: string }) => o?.value !== AUDIO_SELECT_CUSTOM_VALUE
  ) as Array<{ label: string; value: string }>
  return [
    ...presetOptions,
    { label: t('workflowApps.custom'), value: AUDIO_SELECT_CUSTOM_VALUE }
  ]
})

// 默认值 Select 显示值
const defaultValueSelectDisplay = computed(() => {
  if (props.input.type !== 'file.audio.select') return ''
  const val = localInput.value
  if (!val || val === AUDIO_SELECT_CUSTOM_VALUE)
    return AUDIO_SELECT_CUSTOM_VALUE
  const preset = defaultValueAudioOptions.value.find((o) => o.value === val)
  return preset ? val : AUDIO_SELECT_CUSTOM_VALUE
})

const isDefaultValueCustomMode = computed(
  () => defaultValueSelectDisplay.value === AUDIO_SELECT_CUSTOM_VALUE
)

const handleDefaultValueSelectChange = (value: string) => {
  updateInputConfig('value', value)
}

const triggerDefaultValueAudioUpload = () => {
  defaultValueAudioInputRef.value?.click()
}

const handleDefaultValueAudioFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    emit('upload-audio-for-default-value', file)
  }
  target.value = ''
}

const triggerPresetAudioUpload = (optionIndex: number) => {
  pendingPresetUploadIndex.value = optionIndex
  presetAudioInputRef.value?.click()
}

const handlePresetAudioFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  const idx = pendingPresetUploadIndex.value
  if (file && idx !== null) {
    emit('upload-audio-for-option', idx, file)
    pendingPresetUploadIndex.value = null
  }
  target.value = ''
}

// file.audio.select 的预设选项（排除 __custom__），携带原始索引
const presetAudioOptions = computed(() => {
  const opts = localInput.options || []
  return opts
    .map((o, i) => ({ option: o, actualIndex: i }))
    .filter(({ option }) => option?.value !== '__custom__')
})
</script>

<style scoped>
/* 使用项目现有的CSS变量系统 */
.bg-surface-100 {
  background-color: var(--comfy-menu-secondary-bg) !important;
  color: var(--text-color);
}

.text-surface-500 {
  color: var(--text-color-secondary) !important;
}

.text-surface-600 {
  color: var(--text-color) !important;
}

.order-input :deep(.p-inputnumber-input) {
  width: 80px;
}
</style>
